<template>
  <div class="w-full">
      <div class="w-[90%] max-w-5xl mx-auto py-14">
        <h1 class="text-4xl font-semibold mb-2">DODO Dealer EOI</h1>
        <p class="text-grey text-sm mb-8">Dealer-Owned, Dealer-Operated — Expression of Interest Submissions</p>

        <div v-if="loading" class="w-full pt-40 flex items-center justify-center">
          <spinner />
        </div>

        <div class="w-full" v-else>
          <!-- Detail view -->
          <div class="w-full pb-20" v-if="singleActive">
            <div class="w-full mt-2 mb-8">
              <span class="cursor-pointer underline text-sm" @click="closeDetail">← Back to list</span>
            </div>
            <h2 class="text-3xl font-semibold mb-8">{{ singleItem.full_name }}</h2>

            <div class="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <span class="text-sm text-qorayGreen">Full Name</span>
                <div class="font-medium mt-1">{{ singleItem.full_name }}</div>
              </div>
              <div>
                <span class="text-sm text-qorayGreen">Email</span>
                <div class="mt-1"><a :href="'mailto:' + singleItem.email" class="underline">{{ singleItem.email }}</a></div>
              </div>
              <div>
                <span class="text-sm text-qorayGreen">Phone</span>
                <div class="mt-1">{{ singleItem.phone }}</div>
              </div>
              <div>
                <span class="text-sm text-qorayGreen">State of Interest</span>
                <div class="mt-1">{{ singleItem.state_of_interest }}</div>
              </div>
              <div>
                <span class="text-sm text-qorayGreen">Upfront Capital</span>
                <div class="mt-1">{{ singleItem.upfront_capital }}</div>
              </div>
              <div>
                <span class="text-sm text-qorayGreen">Business Background</span>
                <div class="mt-1">{{ singleItem.business_background || '—' }}</div>
              </div>
              <div>
                <span class="text-sm text-qorayGreen">Referral Source</span>
                <div class="mt-1">{{ singleItem.referral_source }}</div>
              </div>
              <div>
                <span class="text-sm text-qorayGreen">Referral Name</span>
                <div class="mt-1">{{ singleItem.referral_name || '—' }}</div>
              </div>
              <div>
                <span class="text-sm text-qorayGreen">Submitted At</span>
                <div class="mt-1">{{ $store.getters.formatDate(singleItem.created_at) }}</div>
              </div>
              <div class="col-span-2 md:col-span-3">
                <span class="text-sm text-qorayGreen">Preferred Location</span>
                <div class="mt-1">{{ singleItem.preferred_location || '—' }}</div>
              </div>
              <div class="col-span-2 md:col-span-3">
                <span class="text-sm text-qorayGreen">Additional Comments</span>
                <div class="mt-1 whitespace-pre-wrap">{{ singleItem.additional_comments || '—' }}</div>
              </div>
            </div>
          </div>

          <!-- List view -->
          <div class="w-full" v-else>
            <!-- Header row: total + filters -->
            <div class="w-full flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <span class="text-sm">
                Total submissions: <span class="font-bold">{{ total }}</span>
              </span>

              <div class="flex flex-wrap items-end gap-3">
                <!-- Search by name -->
                <label class="text-sm text-grey font-medium">
                  Name
                  <br />
                  <input
                    v-model="filters.full_name"
                    placeholder="Search name..."
                    class="px-2 py-1 border border-grey border-opacity-35 rounded-md mt-1"
                  />
                </label>

                <!-- State filter -->
                <label class="text-sm text-grey font-medium">
                  State
                  <br />
                  <select
                    v-model="filters.state_of_interest"
                    class="px-2 py-1 border border-grey border-opacity-35 rounded-md mt-1"
                  >
                    <option value="">All States</option>
                    <option
                      v-for="s in filterOptions.states"
                      :key="s"
                      :value="s"
                      v-text="s"
                    ></option>
                  </select>
                </label>

                <!-- Capital filter -->
                <label class="text-sm text-grey font-medium">
                  Capital
                  <br />
                  <select
                    v-model="filters.upfront_capital"
                    class="px-2 py-1 border border-grey border-opacity-35 rounded-md mt-1"
                  >
                    <option value="">All Ranges</option>
                    <option
                      v-for="c in filterOptions.upfront_capitals"
                      :key="c"
                      :value="c"
                      v-text="c"
                    ></option>
                  </select>
                </label>

                <!-- Source filter -->
                <label class="text-sm text-grey font-medium">
                  Source
                  <br />
                  <select
                    v-model="filters.referral_source"
                    class="px-2 py-1 border border-grey border-opacity-35 rounded-md mt-1"
                  >
                    <option value="">All Sources</option>
                    <option
                      v-for="r in filterOptions.referral_sources"
                      :key="r"
                      :value="r"
                      v-text="r"
                    ></option>
                  </select>
                </label>

                <!-- Sort -->
                <label class="text-sm text-grey font-medium">
                  Sort
                  <br />
                  <select
                    v-model="filters.sort"
                    class="px-2 py-1 border border-grey border-opacity-35 rounded-md mt-1"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </label>

                <!-- Reset -->
                <div v-if="hasActiveFilters">
                  <span
                    @click="resetFilters"
                    class="underline text-xs font-light cursor-pointer"
                    >Reset</span
                  >
                </div>
              </div>
            </div>

            <!-- Table -->
            <table class="w-full text-left">
              <thead>
                <tr class="border-b border-b-grey border-opacity-20">
                  <th class="py-2 text-center pr-3">S/N</th>
                  <th class="py-2">Full Name</th>
                  <th class="py-2">Email</th>
                  <th class="py-2 text-center">State</th>
                  <th class="py-2 text-center">Capital</th>
                  <th class="py-2 text-center">Source</th>
                  <th class="py-2 text-center">Date</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in submissions"
                  :key="item.id"
                  :class="[
                    'border-b border-b-grey border-opacity-20 cursor-pointer hover:bg-grey hover:bg-opacity-45 transition-all',
                    (index + 1) % 2
                      ? 'bg-grey bg-opacity-20'
                      : 'bg-grey bg-opacity-5',
                  ]"
                  @click="openDetail(item)"
                >
                  <td class="py-2 text-center pr-3">{{ index + 1 }}</td>
                  <td class="py-2">{{ item.full_name }}</td>
                  <td class="py-2">{{ item.email }}</td>
                  <td class="py-2 text-center">{{ item.state_of_interest }}</td>
                  <td class="py-2 text-center">{{ item.upfront_capital }}</td>
                  <td class="py-2 text-center">{{ item.referral_source }}</td>
                  <td class="py-2 text-center pr-4">
                    {{ $store.getters.formatDate(item.created_at) }}
                  </td>
                </tr>
                <tr v-if="submissions.length === 0">
                  <td colspan="7" class="py-10 text-center text-grey text-sm">No submissions found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import Spinner from "@/components/Spinner.vue";

export default {
  name: "DodoDealers",
  components: { Spinner },
  data() {
    return {
      submissions: [],
      filterOptions: {
        states: [],
        upfront_capitals: [],
        referral_sources: [],
      },
      filters: {
        full_name: "",
        state_of_interest: "",
        upfront_capital: "",
        referral_source: "",
        sort: "desc",
      },
      singleItem: {},
      singleActive: false,
      loading: true,
      total: 0,
    };
  },
  computed: {
    filterQuery() {
      const params = Object.entries(this.filters)
        .filter(([, value]) => value)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");
      return params ? `?${params}` : "";
    },
    hasActiveFilters() {
      return (
        this.filters.full_name ||
        this.filters.state_of_interest ||
        this.filters.upfront_capital ||
        this.filters.referral_source
      );
    },
  },
  watch: {
    filterQuery() {
      this.fetchSubmissions();
    },
  },
  methods: {
    openDetail(item) {
      this.singleItem = item;
      this.singleActive = true;
    },
    closeDetail() {
      this.singleItem = {};
      this.singleActive = false;
    },
    resetFilters() {
      this.filters = {
        full_name: "",
        state_of_interest: "",
        upfront_capital: "",
        referral_source: "",
        sort: "desc",
      };
    },
    async fetchSubmissions() {
      const payload = { path: "/admin/api/dodo-dealers" + this.filterQuery };
      try {
        const response = await this.$store.dispatch("getRequest", payload);
        this.submissions = response.data.data.submissions;
        this.filterOptions = response.data.data.filter_options;
        this.total = response.data.data.total;
      } catch (error) {
        console.error("Error fetching DODO submissions:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    await this.fetchSubmissions();
  },
};
</script>
